import { useState, useEffect } from "react";
import i18n from "../../i18n";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleRight } from "@fortawesome/free-solid-svg-icons";
import { useProfile, useApplicationProfile } from "../../modules";
import { LoaderAnimated } from '../../components';
import { UserPreferencesModel, ProfileFieldType } from 'outbound-api-client/lib/models';
import { apiService } from '../../services';
import { toast } from 'react-toastify';



export function UserSettings() {
    const { user, getUser } = useProfile(); // to get the user details including the profile data
    const { applicationProfile } = useApplicationProfile(); // to get the additional profile fields defined for the application

    const [showSelector, setShowSelector] = useState(false); // to toggle the display of the settings selector
    const [isSaving, setIsSaving] = useState(false); // to toggle the display of the loader
    const [hasChanges, setHasChanges] = useState(false); // to track if there are unsaved changes and conditionally show the save/cancel buttons

    const [updatedProfileData, setUpdatedProfileData] = useState<UserPreferencesModel>((new UserPreferencesModel())); // to hold the updated profile data before saving

    const t = i18n.t; // translation function


    useEffect(() => {

        if (!user) return;

        // when the user data is loaded, we want to initialize the updatedProfileData state with the user's current profile data so that we can track changes to it
        let newProfileData = new UserPreferencesModel();
        user.profileData?.forEach(pd => {
            if (!applicationProfile?.additionalProfileFields.some(apf => apf.name.toLowerCase() === pd.key.toLowerCase())) return;
            newProfileData.additionalProfileFields.push({
                name: pd.key.charAt(0).toUpperCase() + pd.key.slice(1),
                values: pd.value ? pd.value.split(',') : [],
                allowOther: false,
                profileFieldType: ProfileFieldType.Text
            });
        })
        setUpdatedProfileData(newProfileData);
    }, [applicationProfile, user]);


    const toggleSettings = () => {
        setShowSelector(!showSelector);
    };


    // this function will be called when a checkbox is toggled, it will update the updatedProfileData state with the new values
    const updateProfileData = async (e: React.ChangeEvent<HTMLInputElement>, fieldName: any, fieldValue: any) => {
        setHasChanges(true);

        let tempProfileData = { ...updatedProfileData };

        // if the checkbox is checked, add the value to the corresponding additionalProfileFields entry in updatedProfileData, if it's unchecked we want to remove it
        if (e.target.checked) {

            //check if additionalProfileFields.name already exists in updatedProfileData, if yes add the value to the existing array, if not create a new entry
            let existingField = tempProfileData.additionalProfileFields?.find(apf => apf.name.toLowerCase() === fieldName.toLowerCase());
            if (existingField) {
                existingField.values.push(fieldValue);
            }
            else {
                tempProfileData.additionalProfileFields?.push({
                    name: fieldName,
                    values: [fieldValue],
                    allowOther: false,
                    profileFieldType: ProfileFieldType.Text
                });
            }


        }
        else {
            let existingField = tempProfileData.additionalProfileFields?.find(apf => apf.name.toLowerCase() === fieldName.toLowerCase());
            if (existingField) {
                existingField.values = existingField.values.filter(v => v !== fieldValue);
                // if there are no values left for the field, remove the entire field entry from additionalProfileFields
                if (existingField.values.length === 0) {
                    tempProfileData.additionalProfileFields = tempProfileData.additionalProfileFields?.filter(apf => apf.name.toLowerCase() !== fieldName.toLowerCase());
                }
            }
        }


        setUpdatedProfileData({ ...tempProfileData });


    }

    const saveProfileData = async () => {

        setIsSaving(true);
        try {
            await apiService.instance.updateUserPreferences(updatedProfileData);
            await getUser();


            setIsSaving(false);
            setHasChanges(false);
            setShowSelector(false);
            toast(i18n.exists('profile_save_success') ? t('profile_save_success') : 'Your profile has been updated successfully.',
                { type: "success" });


        } catch (error) {
            setIsSaving(false);
            toast(i18n.exists('profile_save_error') ? t('profile_save_error') : 'An error occurred while saving your profile. Please try again.',
                { type: "error" });
        }

    }


    // if user is not loaded yet, do not show anything (or you could show a loader here)
    if (!user) return;


    // the main render of the component
    return <>
        <div className="user_settings">
            <a href="#!"
                tabIndex={0}
                onKeyUp={(e) => (e.key === 'Enter' || e.key === ' ') && toggleSettings()}
                onClick={(e) => {
                    e.preventDefault(); toggleSettings();
                }}
            >{t("header_user_settings")} <FontAwesomeIcon icon={faAngleRight} /></a>
            {showSelector && <>
                <div className="user_options">

                    {/* <code>
                        <strong>applicationProfile</strong>
                        <pre>
                            {JSON.stringify(applicationProfile, null, 2)}
                        </pre>
                    </code>
                    <code>
                        <strong>user.profileData</strong>
                        <pre>
                            {JSON.stringify(user.profileData, null, 2)}
                        </pre>
                    </code> */}
                    {/* <code>
                        <strong>updatedProfileData</strong>
                        <pre>
                            {JSON.stringify(updatedProfileData, null, 2)}
                        </pre>
                    </code> */}

                    <div> {user?.displayNameSafe}</div>
                    <div tabIndex={0}> {user?.email}</div>
                    <div className="mt-3 position-relative">


                        {applicationProfile?.additionalProfileFields.map((field: any, iField: number) => {
                            return <div key={`field_${iField}`}>
                                <div><b>{field.name}</b></div>
                                <div className="d-flex flex-column">
                                    {field.values.map((fieldValue: string, iFieldValue: number) => {
                                        const profileEntry = user?.profileData?.find(pd => pd.key.toLowerCase() === field.name.toLowerCase());
                                        const selectedValues = profileEntry?.value ? profileEntry.value.split(',') : [];
                                        const isChecked = selectedValues.includes(fieldValue);
                                        return <label key={`fieldValue_${iFieldValue}`} className="d-flex gap-2">
                                            <input type="checkbox" defaultChecked={isChecked} onChange={(e) => updateProfileData(e, field.name, fieldValue)} />{fieldValue}
                                        </label>
                                    })}
                                </div>
                            </div>
                        })}
                        {hasChanges && <div className="d-flex justify-content-between mt-1">
                            <button className="btn btn-secondary" onClick={() => setShowSelector(false)}>
                                {i18n.exists('cancel') ? t('cancel') : 'Cancel'} <FontAwesomeIcon icon={faAngleRight} />
                            </button>
                            <button className="btn btn-primary" onClick={() => saveProfileData()}>
                                {i18n.exists('update') ? t('update') : 'Update'} <FontAwesomeIcon icon={faAngleRight} />
                            </button>
                        </div>}
                        {/*
                        <code>
                            <strong>user.profileData</strong>
                            <pre>
                                {JSON.stringify(user?.profileData, null, 2)}
                            </pre>
                        </code>
                        */}
                        {isSaving && <div className="loading">
                            <LoaderAnimated />
                        </div>}
                    </div>
                </div>
            </>}
        </div>
        {showSelector && <div className="overlay" onClick={toggleSettings}></div >}
    </>


}  